import React from 'react';
import { useTranslation } from 'react-i18next';
import { CopyableText } from 'ui/component/CopyableText.tsx';
import type { VerificationResult } from '#admin/hook/useVerifyDocument.ts';

type Props = {
  document: VerificationResult;
};

function getStatusText(status: string, t: (key: string) => string) {
  switch (status) {
    case 'verified':
      return t('document.status.verified');
    case 'not-verified':
      return t('document.status.notVerified');
    case 'error':
      return t('document.status.error');
    default:
      return t('document.status.unknown');
  }
}

function createCopyText(document, t: (key: string) => string) {
  const { filename, status, timestamp, hash, vaultDocumentInfo, organizationName, creationTimestamp } = document;
  const { encryptionKey, originalUrl, type } = vaultDocumentInfo || {};
  let copyText = '';
  copyText += `${t('document.fields.document')}: ${filename}\n`;
  if (organizationName) copyText += `${t('document.fields.organization')}: ${organizationName}\n`;
  copyText += `${t('document.fields.timestamp')}: ${timestamp}\n`;
  creationTimestamp && (copyText += `${t('document.fields.creationTimestamp')}: ${new Date(Number(document.creationTimestamp) * 1000).toLocaleString()}\n`);
  copyText += `${t('document.fields.sha256')}: ${hash}\n`;
  type && (copyText += `${t('document.fields.type')}: ${type}\n`);
  originalUrl && (copyText += `${t('document.fields.originalUrl')}: ${originalUrl}\n`);
  encryptionKey && (copyText += `${t('document.fields.encryptionKey')}: ${encryptionKey}\n`);
  status && (copyText += `${t('document.fields.status')}: ${getStatusText(status, t)}\n`);
  return copyText;
}

export const VerificationCard: React.FC<Props> = ({ document }) => {
  const { t } = useTranslation();
  const { filename, status, timestamp, hash, organizationName, creationTimestamp } = document;

  const creationDate = new Date(Number(creationTimestamp) * 1000).toLocaleString();
  const copyText = React.useMemo(() => createCopyText(document, t), [document, t]);

  return (
    <div className="relative w-full">
      <CopyableText copyText={copyText} tooltipText={t('actions.copyToClipboard')} className="absolute top-4 right-4" />
      <section className="flex flex-col gap-4 overflow-scroll bg-blue-light p-8">
        {status === 'verified' && (
          <div className="flex flex-row gap-1">
            <span className="body1 min-w-36 shrink grow-0 text-grey-label">{t('document.fields.organization')}</span>
            <span className="body2 shrink-0 grow text-grey-body">{organizationName}</span>
          </div>
        )}
        <div className="flex flex-row gap-1">
          <span className="body1 min-w-36 shrink grow-0 text-grey-label">{t('document.fields.document')}</span>
          <span className="body2 shrink-0 grow text-grey-body">{filename}</span>
        </div>
        <div className="flex flex-row gap-1">
          <span className="body1 min-w-36 shrink grow-0 text-grey-label">{t('document.fields.verificationDate')}</span>
          <span className="body2 shrink-0 grow text-grey-body">{timestamp}</span>
        </div>
        {creationTimestamp !== undefined && (
          <div className="flex flex-row gap-1">
            <span className="body1 min-w-36 shrink grow-0 text-grey-label">{t('document.fields.creationDate')}</span>
            <span className="body2 shrink-0 grow text-grey-body">{creationDate}</span>
          </div>
        )}
        <div className="flex flex-row gap-1">
          <span className="body1 min-w-36 shrink grow-0 text-grey-label">{t('document.fields.sha256')}</span>
          <span className="body2 shrink-0 grow text-grey-body">{hash}</span>
        </div>
      </section>
    </div>
  );
};
